import React, { useEffect, useMemo, useState } from 'react'
import { USER_ROLES } from '../config/security'
import { listarHistoricoAcessos, listarUltimosAcessos } from '../services/usuariosAcessos'
import './Usuarios.css'

const FUNCOES = [
  { value: 'administrador', label: 'Administrador' },
  { value: 'administrativo', label: 'Administrativo' },
  { value: 'instalador', label: 'Instalador' },
  { value: 'especialista', label: 'Especialista' },
  { value: 'gerente', label: 'Gerente' },
  { value: 'gerente_comercial', label: 'Gerente Comercial' },
  { value: 'homologador', label: 'Homologador' },
  { value: 'campo', label: 'Campo' },
  { value: 'vistoriador', label: 'Vistoriador' },
  { value: 'pap', label: 'PAP' },
  { value: 'sdr', label: 'SDR' },
  { value: 'avulso', label: 'Avulso' }
]

const LABEL_FUNCAO = FUNCOES.reduce((acc, item) => {
  acc[item.value] = item.label
  return acc
}, {})

function formatarDataHora(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function resumoDispositivo(ua) {
  if (!ua) return '—'
  const mobile = /Mobile|Android|iPhone|iPad/i.test(ua)
  let browser = 'Navegador'
  if (/Edg/i.test(ua)) browser = 'Edge'
  else if (/Chrome/i.test(ua)) browser = 'Chrome'
  else if (/Firefox/i.test(ua)) browser = 'Firefox'
  else if (/Safari/i.test(ua)) browser = 'Safari'
  return `${mobile ? 'Celular' : 'Computador'} · ${browser}`
}

function inicioPeriodo(filtro) {
  const agora = new Date()
  if (filtro === 'hoje') {
    agora.setHours(0, 0, 0, 0)
    return agora
  }
  if (filtro === '7d') {
    agora.setDate(agora.getDate() - 7)
    return agora
  }
  if (filtro === '30d') {
    agora.setDate(agora.getDate() - 30)
    return agora
  }
  return null
}

export default function Usuarios({
  onBack,
  userRole,
  usuarios = [],
  empresas = [],
  onNovo,
  onEdit,
  onDelete,
  onToggleChatZap,
  onToggleWhatsappGestor,
  onToggleWhatsappProposta
}) {
  const isAdmin = userRole === USER_ROLES.ADMINISTRADOR
  const [filtroBusca, setFiltroBusca] = useState('')
  const [filtroFuncao, setFiltroFuncao] = useState('')
  const [filtroStatus, setFiltroStatus] = useState('')
  const [filtroEmpresa, setFiltroEmpresa] = useState('')
  const [filtroAcesso, setFiltroAcesso] = useState('')
  const [ultimosAcessos, setUltimosAcessos] = useState({})
  const [usuarioHistorico, setUsuarioHistorico] = useState(null)
  const [acessos, setAcessos] = useState([])
  const [carregandoHistorico, setCarregandoHistorico] = useState(false)
  const [erroHistorico, setErroHistorico] = useState('')

  const nomeEmpresa = (id) => {
    if (!id) return 'Sem empresa'
    return empresas.find((e) => String(e.id) === String(id))?.empresa || 'Empresa não encontrada'
  }

  useEffect(() => {
    let cancelado = false
    const ids = (usuarios || []).map((u) => u.id).filter(Boolean)
    if (ids.length === 0) {
      setUltimosAcessos({})
      return undefined
    }
    listarUltimosAcessos(ids)
      .then((mapa) => {
        if (!cancelado) setUltimosAcessos(mapa)
      })
      .catch((err) => {
        console.warn('Erro ao carregar últimos acessos:', err?.message || err)
        if (!cancelado) setUltimosAcessos({})
      })
    return () => {
      cancelado = true
    }
  }, [usuarios])

  const usuariosFiltrados = useMemo(() => {
    const busca = filtroBusca.trim().toLowerCase()
    const desde = inicioPeriodo(filtroAcesso)
    return (usuarios || []).filter((usuario) => {
      if (busca) {
        const nome = (usuario.nome || '').toLowerCase()
        const email = (usuario.email || '').toLowerCase()
        if (!nome.includes(busca) && !email.includes(busca) && !String(usuario.telefone || '').replace(/\D/g, '').includes(busca.replace(/\D/g, '') || busca)) return false
      }
      if (filtroFuncao && usuario.role !== filtroFuncao) return false
      if (filtroStatus && usuario.status !== filtroStatus) return false
      if (filtroEmpresa && String(usuario.empresa_id || '') !== String(filtroEmpresa)) return false
      const ultimo = ultimosAcessos[usuario.id]?.data_acesso
      if (filtroAcesso === 'nunca' && ultimo) return false
      if (desde && (!ultimo || new Date(ultimo) < desde)) return false
      return true
    })
  }, [usuarios, filtroBusca, filtroFuncao, filtroStatus, filtroEmpresa, filtroAcesso, ultimosAcessos])

  const limparFiltros = () => {
    setFiltroBusca('')
    setFiltroFuncao('')
    setFiltroStatus('')
    setFiltroEmpresa('')
    setFiltroAcesso('')
  }

  const abrirHistorico = async (usuario) => {
    setUsuarioHistorico(usuario)
    setAcessos([])
    setErroHistorico('')
    setCarregandoHistorico(true)
    try {
      const lista = await listarHistoricoAcessos(usuario.id)
      setAcessos(lista)
    } catch (err) {
      setErroHistorico(err?.message || 'Não foi possível carregar o histórico. Execute o script database/149_create_usuarios_acessos.sql no Supabase.')
    } finally {
      setCarregandoHistorico(false)
    }
  }

  const fecharHistorico = () => {
    setUsuarioHistorico(null)
    setAcessos([])
    setErroHistorico('')
  }

  const renderAcoes = (usuario) => (
    <div className="usuarios-acoes">
      <button type="button" className="action-btn small" onClick={() => onEdit(usuario)} title="Editar usuário">
        Editar
      </button>
      <button type="button" className="action-btn info" onClick={() => abrirHistorico(usuario)} title="Histórico de acessos">
        Acessos
      </button>
      <button type="button" className="action-btn small danger" onClick={() => onDelete(usuario)} title="Excluir usuário">
        Remover
      </button>
    </div>
  )

  return (
    <div className="menu-content usuarios-tela">
      <div className="menu-header">
        <div>
          <button type="button" className="usuarios-voltar" onClick={onBack}>
            ← Voltar ao Dashboard
          </button>
          <h2>Gerenciamento de Usuários</h2>
        </div>
        <button type="button" onClick={onNovo} className="action-button primary">
          + Novo Usuário
        </button>
      </div>

      <div className="usuarios-filtros">
        <div className="usuarios-filtro">
          <label htmlFor="usuarios-busca">Nome ou e-mail</label>
          <input
            id="usuarios-busca"
            type="search"
            className="form-control"
            placeholder="Buscar..."
            value={filtroBusca}
            onChange={(e) => setFiltroBusca(e.target.value)}
          />
        </div>
        <div className="usuarios-filtro">
          <label htmlFor="usuarios-funcao">Função</label>
          <select
            id="usuarios-funcao"
            className="form-select"
            value={filtroFuncao}
            onChange={(e) => setFiltroFuncao(e.target.value)}
          >
            <option value="">Todas</option>
            {FUNCOES.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
        </div>
        <div className="usuarios-filtro">
          <label htmlFor="usuarios-status">Status</label>
          <select
            id="usuarios-status"
            className="form-select"
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
          >
            <option value="">Todos</option>
            <option value="ativo">Ativo</option>
            <option value="inativo">Inativo</option>
          </select>
        </div>
        {isAdmin && (
          <div className="usuarios-filtro">
            <label htmlFor="usuarios-empresa">Empresa</label>
            <select
              id="usuarios-empresa"
              className="form-select"
              value={filtroEmpresa}
              onChange={(e) => setFiltroEmpresa(e.target.value)}
            >
              <option value="">Todas</option>
              {empresas.map((emp) => (
                <option key={emp.id} value={emp.id}>{emp.empresa}</option>
              ))}
            </select>
          </div>
        )}
        <div className="usuarios-filtro">
          <label htmlFor="usuarios-acesso">Último acesso</label>
          <select
            id="usuarios-acesso"
            className="form-select"
            value={filtroAcesso}
            onChange={(e) => setFiltroAcesso(e.target.value)}
          >
            <option value="">Todos</option>
            <option value="hoje">Hoje</option>
            <option value="7d">Últimos 7 dias</option>
            <option value="30d">Últimos 30 dias</option>
            <option value="nunca">Nunca acessou</option>
          </select>
        </div>
        <div className="usuarios-filtro usuarios-filtro-acao">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={limparFiltros}>
            Limpar
          </button>
        </div>
      </div>

      <div className="usuarios-list">
        <h3>Usuários ({usuariosFiltrados.length})</h3>

        <div className="usuarios-tabela-wrap table-container">
          <table className="data-table usuarios-tabela">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Email</th>
                <th>Telefone</th>
                <th>Função</th>
                <th>Status</th>
                <th>Empresa</th>
                <th>Último acesso</th>
                <th>Chat Zap</th>
                <th>Painel gestor</th>
                <th>Aba proposta</th>
                <th>Template WA</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {usuariosFiltrados.length === 0 ? (
                <tr>
                  <td colSpan="12" className="no-data">
                    Nenhum usuário encontrado com os filtros.
                  </td>
                </tr>
              ) : (
                usuariosFiltrados.map((usuario) => (
                  <tr key={usuario.id}>
                    <td>{usuario.nome}</td>
                    <td className="usuarios-email">{usuario.email}</td>
                    <td>{usuario.telefone || '—'}</td>
                    <td>
                      <span className={`role-badge ${usuario.role || ''}`}>
                        {LABEL_FUNCAO[usuario.role] || usuario.role || 'N/A'}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${usuario.status === 'ativo' ? 'status-ativo' : 'status-inativo'}`}>
                        {usuario.status === 'ativo' ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td>
                      <span className={`empresa-badge ${usuario.empresa_id ? '' : 'no-empresa'}`}>
                        {nomeEmpresa(usuario.empresa_id)}
                      </span>
                    </td>
                    <td>{formatarDataHora(ultimosAcessos[usuario.id]?.data_acesso)}</td>
                    <td>
                      <label className="usuarios-chat-zap" title="Liberar no Chat Zap (atendimento WhatsApp)">
                        <input
                          type="checkbox"
                          checked={Number(usuario.chat_zap) === 1}
                          onChange={(e) => onToggleChatZap && onToggleChatZap(usuario, e.target.checked)}
                          disabled={!onToggleChatZap || !isAdmin}
                        />
                        <span>{Number(usuario.chat_zap) === 1 ? 'Liberado' : 'Não'}</span>
                      </label>
                    </td>
                    <td>
                      <label className="usuarios-chat-zap" title="Liberar Painel gestor (todas as conversas)">
                        <input
                          type="checkbox"
                          checked={Number(usuario.whatsapp_gestor) === 1}
                          onChange={(e) => onToggleWhatsappGestor && onToggleWhatsappGestor(usuario, e.target.checked)}
                          disabled={!onToggleWhatsappGestor || !isAdmin}
                        />
                        <span>{Number(usuario.whatsapp_gestor) === 1 ? 'Liberado' : 'Não'}</span>
                      </label>
                    </td>
                    <td>
                      <label className="usuarios-chat-zap" title="Liberar aba Proposta no API Whatsapp">
                        <input
                          type="checkbox"
                          checked={Number(usuario.whatsapp_proposta) === 1}
                          onChange={(e) => onToggleWhatsappProposta && onToggleWhatsappProposta(usuario, e.target.checked)}
                          disabled={!onToggleWhatsappProposta || !isAdmin}
                        />
                        <span>{Number(usuario.whatsapp_proposta) === 1 ? 'Liberado' : 'Não'}</span>
                      </label>
                    </td>
                    <td className="usuarios-template-wa">
                      {usuario.whatsapp_template_nome
                        ? `${usuario.whatsapp_template_nome}${usuario.whatsapp_template_language ? ` (${usuario.whatsapp_template_language})` : ''}`
                        : (usuario.whatsapp_template_id || '—')}
                    </td>
                    <td>{renderAcoes(usuario)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="usuarios-cards">
          {usuariosFiltrados.length === 0 ? (
            <p className="usuarios-vazio">Nenhum usuário encontrado com os filtros.</p>
          ) : (
            usuariosFiltrados.map((usuario) => (
              <article key={usuario.id} className="usuarios-card">
                <header className="usuarios-card-topo">
                  <div>
                    <h4>{usuario.nome}</h4>
                    <p className="usuarios-email">{usuario.email}</p>
                    {usuario.telefone && <p className="usuarios-email">{usuario.telefone}</p>}
                  </div>
                  <span className={`status-badge ${usuario.status === 'ativo' ? 'status-ativo' : 'status-inativo'}`}>
                    {usuario.status === 'ativo' ? 'Ativo' : 'Inativo'}
                  </span>
                </header>
                <dl className="usuarios-card-dados">
                  <div>
                    <dt>Função</dt>
                    <dd>
                      <span className={`role-badge ${usuario.role || ''}`}>
                        {LABEL_FUNCAO[usuario.role] || usuario.role || 'N/A'}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt>Empresa</dt>
                    <dd>{nomeEmpresa(usuario.empresa_id)}</dd>
                  </div>
                  <div>
                    <dt>Último acesso</dt>
                    <dd>{formatarDataHora(ultimosAcessos[usuario.id]?.data_acesso)}</dd>
                  </div>
                  <div>
                    <dt>Chat Zap</dt>
                    <dd>
                      <label className="usuarios-chat-zap">
                        <input
                          type="checkbox"
                          checked={Number(usuario.chat_zap) === 1}
                          onChange={(e) => onToggleChatZap && onToggleChatZap(usuario, e.target.checked)}
                          disabled={!onToggleChatZap || !isAdmin}
                        />
                        <span>{Number(usuario.chat_zap) === 1 ? 'Liberado' : 'Não'}</span>
                      </label>
                    </dd>
                  </div>
                  <div>
                    <dt>Painel gestor</dt>
                    <dd>
                      <label className="usuarios-chat-zap">
                        <input
                          type="checkbox"
                          checked={Number(usuario.whatsapp_gestor) === 1}
                          onChange={(e) => onToggleWhatsappGestor && onToggleWhatsappGestor(usuario, e.target.checked)}
                          disabled={!onToggleWhatsappGestor || !isAdmin}
                        />
                        <span>{Number(usuario.whatsapp_gestor) === 1 ? 'Liberado' : 'Não'}</span>
                      </label>
                    </dd>
                  </div>
                  <div>
                    <dt>Aba proposta</dt>
                    <dd>
                      <label className="usuarios-chat-zap">
                        <input
                          type="checkbox"
                          checked={Number(usuario.whatsapp_proposta) === 1}
                          onChange={(e) => onToggleWhatsappProposta && onToggleWhatsappProposta(usuario, e.target.checked)}
                          disabled={!onToggleWhatsappProposta || !isAdmin}
                        />
                        <span>{Number(usuario.whatsapp_proposta) === 1 ? 'Liberado' : 'Não'}</span>
                      </label>
                    </dd>
                  </div>
                  <div>
                    <dt>Template WA</dt>
                    <dd>
                      {usuario.whatsapp_template_nome
                        ? `${usuario.whatsapp_template_nome}${usuario.whatsapp_template_language ? ` (${usuario.whatsapp_template_language})` : ''}`
                        : (usuario.whatsapp_template_id || '—')}
                    </dd>
                  </div>
                  <div>
                    <dt>Multiempresas</dt>
                    <dd>{usuario.multiempresas ? 'Sim' : 'Não'}</dd>
                  </div>
                </dl>
                {renderAcoes(usuario)}
              </article>
            ))
          )}
        </div>
      </div>

      {usuarioHistorico && (
        <div className="usuarios-modal-overlay" onClick={fecharHistorico} role="presentation">
          <div
            className="usuarios-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="usuarios-historico-titulo"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="usuarios-modal-header">
              <div>
                <h3 id="usuarios-historico-titulo">Histórico de acessos</h3>
                <p>{usuarioHistorico.nome} · {usuarioHistorico.email}</p>
              </div>
              <button type="button" className="close-button" aria-label="Fechar" onClick={fecharHistorico}>
                ×
              </button>
            </div>
            <div className="usuarios-modal-body">
              {carregandoHistorico && <p className="text-muted mb-0">Carregando...</p>}
              {erroHistorico && <div className="alert alert-danger py-2">{erroHistorico}</div>}
              {!carregandoHistorico && !erroHistorico && acessos.length === 0 && (
                <p className="text-muted mb-0">Nenhum acesso registrado para este usuário.</p>
              )}
              {!carregandoHistorico && acessos.length > 0 && (
                <ul className="usuarios-historico-lista">
                  {acessos.map((item) => (
                    <li key={item.id}>
                      <strong>{formatarDataHora(item.data_acesso)}</strong>
                      <span>{resumoDispositivo(item.user_agent)}</span>
                      <span className="usuarios-historico-origem">{item.origem || 'web'}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
